import React, { useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { validateReason } from '../../utils/brand-live';
import DialogComponent from '../dialog/dialog.component';

// Hộp nhập lý do dùng cho phía doanh nghiệp: báo gian lận một buổi live, hoặc xin huỷ phần livestream.

const errorMessage = (err: unknown, fallback: string) =>
  (err as { response?: { data?: { message?: string } } })?.response?.data?.message || fallback;

export interface BrandLiveReasonDialogProps {
  title: string;
  hint?: string;
  placeholder: string;
  submitLabel: string;
  min?: number;
  max?: number;
  failMessage: string;
  /** Gửi lên máy chủ; trả về câu thông báo thành công. */
  onSubmit: (text: string) => Promise<string>;
  onClose: () => void;
  onDone: () => void;
}

export const BrandLiveReasonDialog: React.FC<BrandLiveReasonDialogProps> = ({
  title,
  hint,
  placeholder,
  submitLabel,
  min = 10,
  max = 1000,
  failMessage,
  onSubmit,
  onClose,
  onDone,
}) => {
  const [text, setText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const lock = useRef(false);

  const submit = async () => {
    if (lock.current) return;
    const invalid = validateReason(text, min, max);
    if (invalid) {
      toast.error(invalid);
      return;
    }
    lock.current = true;
    setSubmitting(true);
    try {
      toast.success(await onSubmit(text.trim()));
      onDone();
    } catch (err) {
      toast.error(errorMessage(err, failMessage));
    } finally {
      lock.current = false;
      setSubmitting(false);
    }
  };

  return (
    <DialogComponent visible title={title} onClose={() => !submitting && onClose()} hideOnClickOutside={false}>
      <div className="flex flex-col gap-3 p-4">
        {hint && <p className="ui-muted text-[13px]">{hint}</p>}
        <textarea
          className="ui-input min-h-[110px] w-full px-3 py-2 text-[14px]"
          placeholder={placeholder}
          value={text}
          maxLength={max}
          onChange={(e) => setText(e.target.value)}
        />
        <div className="ui-muted text-right text-[12px]">
          {text.trim().length}/{max}
        </div>
        <div className="flex justify-end gap-2">
          <button type="button" disabled={submitting} onClick={onClose} className="ui-text rounded-lg border ui-divider px-4 py-2 text-[14px]">
            Đóng
          </button>
          <button
            type="button"
            disabled={submitting}
            onClick={submit}
            className="rounded-lg bg-[#0057FF] px-4 py-2 text-[14px] font-semibold text-white disabled:opacity-60"
          >
            {submitLabel}
          </button>
        </div>
      </div>
    </DialogComponent>
  );
};

/** Doanh nghiệp báo gian lận một buổi live. */
const BrandLiveReportDialog: React.FC<{
  onSubmit: (reason: string) => Promise<unknown>;
  onClose: () => void;
  onDone: () => void;
}> = ({ onSubmit, onClose, onDone }) => (
  <BrandLiveReasonDialog
    title="Báo gian lận buổi live"
    hint="Mô tả điều bạn thấy bất thường ở buổi này. Creator có 3 ngày để giải trình, sau đó admin sẽ quyết."
    placeholder="Lý do báo gian lận"
    submitLabel="Gửi báo cáo"
    failMessage="Không gửi được báo cáo."
    onSubmit={async (t) => {
      await onSubmit(t);
      return 'Đã gửi báo cáo gian lận.';
    }}
    onClose={onClose}
    onDone={onDone}
  />
);

export default BrandLiveReportDialog;
